import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useHotelChainData } from '@components/App';
import BookingTypeTabPanel, { BookingTypeValues } from '@components/BookingType';
import { SearchForm, useSearchForm } from '@components/SearchForm';
import CenteredContainer from '@containers/CenteredContainer';
import { useSearchParams } from '@router';
import { normalize } from '@spazious/jsonapi-tools';
import { useGetHotel } from '@spazious/react-booking-engine';
// UIKit Components
import { Box, FlexBox, FlexBoxProps } from '@spazious/react-components';

import { searchParamsToBookingType } from '../SearchForm/SearchFormUtils.js';

export type HeaderProps = {
  condensed?: boolean;
  direction?: 'row' | 'column';
  customSearchBar?: 'dollhouse';
} & FlexBoxProps;

export const BOX_SHADOW_CONDENSED = 'rgba(0, 0, 0, 0.1) 0px 1px 5px 1px';
export const BOX_SHADOW_DEFAULT = 'rgba(0, 0, 0, 0) 0px 0px 0px 0px';
export const BOX_TRANSITION = 'box-shadow 0.2s cubic-bezier(0.2,0,0,1)';

export const Header: React.FC<HeaderProps> = ({
  condensed,
  direction = 'row',
  containerRef,
  ...restProps
}) => {
  const [searchParams] = useSearchParams();
  const { hotelSlug } = useParams();
  const hotelChainData = useHotelChainData();

  const [{ bookingType }, { setBookingType }] = useSearchForm();

  const setInitialBookingType = useCallback(
    (hasPublicGuestRooms: boolean, hasPublicMeetingRooms: boolean) => {
      if (bookingType) {
        return;
      }
      if (searchParams.get('bookingType')) {
        const bookingTypeValue = searchParamsToBookingType(
          searchParams.get('bookingType') as string
        );
        bookingTypeValue && setBookingType(bookingTypeValue);
        return;
      }

      const bookingTypeValue =
        hasPublicGuestRooms && hasPublicMeetingRooms
          ? BookingTypeValues.AccommodationAndSpaces
          : hasPublicGuestRooms
          ? BookingTypeValues.OnlyAccommodations
          : hasPublicMeetingRooms
          ? BookingTypeValues.OnlySpaces
          : null;

      bookingTypeValue && setBookingType(bookingTypeValue);
    },
    [bookingType, setBookingType, searchParams]
  );

  const normalizedHotelChainData = useMemo(
    () => (hotelChainData?.data ? normalize(hotelChainData?.data) : {}),
    [hotelChainData?.data]
  );

  const chosenHotel = useGetHotel({
    variables: {
      hotelId: hotelSlug!,
    },
    skip: !hotelSlug,
    refetchOnWindowFocus: false,
  });

  const normalizedSelectedHotel = useMemo(
    () => (chosenHotel?.data ? normalize(chosenHotel?.data) : {}),
    [chosenHotel?.data]
  );

  const tabsInfo = useMemo(() => {
    const hasGuestRooms = hotelSlug
      ? !!normalizedSelectedHotel.hasPublicGuestRooms
      : !!normalizedHotelChainData.hasPublicGuestRooms;
    const hasMeetingRooms = hotelSlug
      ? !!normalizedSelectedHotel?.hasPublicMeetingRooms
      : !!normalizedHotelChainData?.hasPublicMeetingRooms;
    const hasGuestRoomsAndMeetingRooms = hasGuestRooms && hasMeetingRooms;

    const tabCount = [hasGuestRoomsAndMeetingRooms, hasGuestRooms, hasMeetingRooms].filter(
      Boolean
    ).length;
    return {
      hasGuestRooms,
      hasMeetingRooms,
      hasGuestRoomsAndMeetingRooms,
      tabCount,
    };
  }, [
    hotelSlug,
    normalizedHotelChainData.hasPublicGuestRooms,
    normalizedHotelChainData?.hasPublicMeetingRooms,
    normalizedSelectedHotel.hasPublicGuestRooms,
    normalizedSelectedHotel?.hasPublicMeetingRooms,
  ]);

  const handleTabClick = useCallback(
    (bookingTypeValue: BookingTypeValues) => {
      setBookingType(bookingTypeValue);
    },
    [setBookingType]
  );

  useEffect(() => {
    if (hotelChainData?.data) {
      const meta = hotelChainData?.data?.data?.meta ?? {};
      const { hasPublicGuestRooms = false, hasPublicMeetingRooms = false } = meta;
      setInitialBookingType(hasPublicGuestRooms, hasPublicMeetingRooms);
    }
  }, [hotelChainData?.data, setInitialBookingType]);

  useEffect(() => {
    if (chosenHotel?.data) {
      const meta = chosenHotel?.data?.data?.meta ?? {};
      const { hasPublicGuestRooms = false, hasPublicMeetingRooms = false } = meta;
      setInitialBookingType(hasPublicGuestRooms, hasPublicMeetingRooms);
    }
  }, [chosenHotel?.data, setInitialBookingType]);

  return (
    <FlexBox zIndex="1000" {...restProps}>
      <FlexBox
        style={{
          transition: BOX_TRANSITION,
          boxShadow: BOX_SHADOW_DEFAULT,
        }}
      >
        <CenteredContainer marginTop={1}>
          <BookingTypeTabPanel tabsInfo={tabsInfo} onClick={handleTabClick} />
        </CenteredContainer>
        <Box component="header" direction={direction === 'column' ? 'column-reverse' : 'row'}>
          <CenteredContainer ref={containerRef} alignItems="center" direction="row" zIndex="100">
            <FlexBox flex="1">
              <SearchForm condensed={condensed} direction={direction} />
            </FlexBox>
          </CenteredContainer>
        </Box>
      </FlexBox>
    </FlexBox>
  );
};

export default Header;
